import React, { useEffect } from 'react';

import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { Stack } from '@atlaskit/primitives/compiled';
import Heading from '@atlaskit/heading';

import { CategorySelector, type Category } from '@confluence/category-selector';
import { DebouncedSearchBar } from '@confluence/debounced-search-bar';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	SNIPPET_CATEGORY_IDS,
	useSnippetFilterCategories,
} from '../constants/snippetCategories.ts';
import type { Snippet } from '../snippetTypes.ts';
import { useSnippets } from '../store/snippetsStore.ts';
import { SnippetPanelList } from './SnippetPanelList.tsx';
import { SnippetPanelSkeleton } from './skeleton/SnippetPanelSkeleton.tsx';

const messages = defineMessages({
	snippetsRecommended: {
		id: 'snippets.panel.recommended',
		defaultMessage: 'Recommended',
		description: 'Header for the list of Block Templates within the right panel',
	},
	searchSnippetsPlaceholderText: {
		id: 'snippets.panel.search.placeholderText',
		defaultMessage: 'Search author, category, topic',
		description: 'The placeholder text in the templates search box, visible when it is empty',
	},
});

export const SnippetPanel = () => {
	const { userId } = useSessionData();
	const intl = useIntl();

	const [{ isLoading, selectedCategoryId, searchQuery }, snippetsActions] = useSnippets();

	const categories: Category[] = useSnippetFilterCategories();
	const filteredSnippets: Snippet[] = snippetsActions.getFilteredSnippets(userId);

	useEffect(() => {
		snippetsActions.resetFilters();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const shouldShowRecommended =
		selectedCategoryId === SNIPPET_CATEGORY_IDS.ALL && searchQuery === '';

	return isLoading ? (
		<SnippetPanelSkeleton />
	) : (
		<Stack space="space.200" grow="fill">
			<DebouncedSearchBar
				placeholder={intl.formatMessage(messages.searchSnippetsPlaceholderText)}
				isCompact
				onChange={(query) => snippetsActions.setSearchQuery(query)}
				initialValue={searchQuery || ''}
			/>
			<CategorySelector
				categories={categories}
				selectedCategoryIds={[selectedCategoryId]}
				onCategorySelected={(category) =>
					snippetsActions.setCategoryFilter(category.id as SNIPPET_CATEGORY_IDS)
				}
			/>
			{shouldShowRecommended && (
				<Heading size="xxsmall">
					<FormattedMessage {...messages.snippetsRecommended} />
				</Heading>
			)}
			<SnippetPanelList snippets={filteredSnippets} />
		</Stack>
	);
};
